import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Stack,
  Switch,
  Text,
  Textarea,
  notify,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { MdCancel, MdEdit, MdSave } from "react-icons/md";
import { authService } from "services/authService";
import { Company, companyService } from "services/companyService";

const CompanyDetails = () => {
  const { t } = useTranslation();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [memoryEnabled, setMemoryEnabled] = useState(false);
  const [require2FA, setRequire2FA] = useState(false);
  const [saving2fa, setSaving2fa] = useState(false);

  const currentUser = authService.getCurrentUser();
  const companyId = currentUser?.companyId || "";
  const isAdmin = currentUser?.role === "Admin";

  useEffect(() => {
    const loadCompany = async () => {
      if (!companyId) return;

      try {
        setLoading(true);
        const data = await companyService.getCompany(companyId);
        setCompany(data);
        setMemoryEnabled(data.memoryEnabled ?? false);
        setRequire2FA(data.require2FA ?? false);
        setError("");
      } catch (err: any) {
        setError(err.response?.data?.message || t("app.company.loadFailed"));
      } finally {
        setLoading(false);
      }
    };

    loadCompany();
  }, [companyId, t]);

  const loadCompany = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const data = await companyService.getCompany(companyId);
      setCompany(data);
      setMemoryEnabled(data.memoryEnabled ?? false);
      setRequire2FA(data.require2FA ?? false);
      setError("");
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.company.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async (checked: boolean) => {
    if (!companyId) return;
    setSaving2fa(true);
    try {
      const updated = await companyService.update2faPolicy(companyId, checked);
      setRequire2FA(updated.require2FA ?? checked);
      notify({
        type: "success",
        title: t("app.twoFactor.companyPolicyUpdated"),
      });
    } catch (err: any) {
      notify({
        type: "error",
        title: t("app.errors.requestFailed"),
        description: err.response?.data?.message,
      });
    } finally {
      setSaving2fa(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company) return;

    const name =
      (document.getElementById("companyName") as HTMLInputElement)?.value || "";
    const description =
      (document.getElementById("description") as HTMLTextAreaElement)?.value ||
      "";
    const address =
      (document.getElementById("address") as HTMLInputElement)?.value || "";
    const phone =
      (document.getElementById("phone") as HTMLInputElement)?.value || "";
    const email =
      (document.getElementById("email") as HTMLInputElement)?.value || "";
    const website =
      (document.getElementById("website") as HTMLInputElement)?.value || "";
    // An empty box means "keep the stored key", not "clear it" — the server skips a blank value.
    const openAiApiKey =
      (document.getElementById("openAiApiKey") as HTMLInputElement)?.value ||
      null;
    const geminiApiKey =
      (document.getElementById("geminiApiKey") as HTMLInputElement)?.value ||
      null;

    setSaving(true);
    try {
      const updated = await companyService.updateCompany(companyId, {
        name,
        description,
        address,
        phone,
        email,
        website,
        openAiApiKey,
        geminiApiKey,
        memoryEnabled,
      });
      setCompany(updated);
      setMemoryEnabled(updated.memoryEnabled ?? false);
      setIsEditing(false);
      setSuccess(t("app.company.saveSuccess"));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.company.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadCompany();
  };

  /**
   * The stored key is never sent back to the browser, so the field starts empty even when a key
   * exists. These two helpers make that state legible: the user sees whether a key is saved, and
   * that leaving the box blank keeps it rather than clearing it.
   */
  const keyHelperText = (hasKey: boolean) => {
    if (!isEditing) {
      return hasKey
        ? t("app.company.aiKeys.configured")
        : t("app.company.aiKeys.notConfigured");
    }
    return hasKey
      ? t("app.company.aiKeys.replaceHelp")
      : t("app.company.aiKeys.addHelp");
  };

  const keyPlaceholder = (hasKey: boolean, emptyPlaceholder: string) =>
    hasKey ? t("app.company.aiKeys.replacePlaceholder") : emptyPlaceholder;

  if (loading) {
    return (
      <Card.Root>
        <Card.Body>
          <Text textAlign="center" color={{ base: "gray.600", _dark: "white" }}>
            {t("app.company.loading")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  if (!company) {
    return (
      <Card.Root>
        <Card.Body>
          <Text textAlign="center" color="red.600">
            {t("app.company.notFound")}
          </Text>
        </Card.Body>
      </Card.Root>
    );
  }

  return (
    <Flex direction="column" w="full" gap={5}>
      <Flex justify="space-between" align="center">
        <Heading
          size="2xl"
          fontWeight="bold"
          color={{ base: "navy.700", _dark: "white" }}
        >
          {t("app.company.title")}
        </Heading>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.company")} />
          {!isEditing && isAdmin && (
            <Button
              onClick={() => setIsEditing(true)}
              colorPalette="blue"
              variant="solid"
            >
              <MdEdit size="20px" />
              {t("app.company.editDetails")}
            </Button>
          )}
        </HStack>
      </Flex>

      {error && (
        <Box borderRadius="lg" bg="red.100" p={3}>
          <Text fontSize="sm" color="red.700">
            {error}
          </Text>
        </Box>
      )}

      {success && (
        <Box borderRadius="lg" bg="green.100" p={3}>
          <Text fontSize="sm" color="green.700">
            {success}
          </Text>
        </Box>
      )}

      <Card.Root>
        <Card.Body p={6}>
          <form onSubmit={handleSave}>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Input
                  label={t("app.company.name")}
                  placeholder={t("app.company.namePlaceholder")}
                  id="companyName"
                  type="text"
                  disabled={!isEditing}
                  defaultValue={company.name}
                />
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Textarea
                  label={t("app.company.description")}
                  placeholder={t("app.company.descriptionPlaceholder")}
                  id="description"
                  disabled={!isEditing}
                  defaultValue={company.description || ""}
                  rows={3}
                />
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Input
                  label={t("app.company.address")}
                  placeholder={t("app.company.addressPlaceholder")}
                  id="address"
                  type="text"
                  disabled={!isEditing}
                  defaultValue={company.address || ""}
                />
              </GridItem>

              <GridItem>
                <Input
                  label={t("app.company.phone")}
                  placeholder={t("app.company.phonePlaceholder")}
                  id="phone"
                  type="tel"
                  disabled={!isEditing}
                  defaultValue={company.phone || ""}
                />
              </GridItem>

              <GridItem>
                <Input
                  label={t("app.company.email")}
                  placeholder={t("app.company.emailPlaceholder")}
                  id="email"
                  type="email"
                  disabled={!isEditing}
                  defaultValue={company.email || ""}
                />
              </GridItem>

              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Input
                  label={t("app.company.website")}
                  placeholder={t("app.company.websitePlaceholder")}
                  id="website"
                  type="url"
                  disabled={!isEditing}
                  defaultValue={company.website || ""}
                />
              </GridItem>

              {/* AI Provider API Keys Section */}
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Box
                  borderWidth="1px"
                  borderColor={{ base: "gray.200", _dark: "whiteAlpha.200" }}
                  borderRadius="md"
                  p={4}
                  bg={{ base: "gray.50", _dark: "whiteAlpha.50" }}
                >
                  <Heading
                    size="sm"
                    mb={3}
                    color={{ base: "navy.700", _dark: "white" }}
                  >
                    {t("app.company.aiProviderKeys")}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color={{ base: "gray.600", _dark: "gray.400" }}
                    mb={4}
                  >
                    {t("app.company.aiProviderKeysDescription")}
                  </Text>
                  <Grid
                    templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                    gap={4}
                  >
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Input
                        label={t("app.company.openAiApiKey")}
                        placeholder={keyPlaceholder(
                          company.hasOpenAiKey === true,
                          t("app.company.openAiApiKeyPlaceholder"),
                        )}
                        id="openAiApiKey"
                        type="password"
                        autoComplete="off"
                        disabled={!isEditing}
                        defaultValue=""
                        helperText={keyHelperText(
                          company.hasOpenAiKey === true,
                        )}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Input
                        label={t("app.company.geminiApiKey")}
                        placeholder={keyPlaceholder(
                          company.hasGeminiKey === true,
                          t("app.company.geminiApiKeyPlaceholder"),
                        )}
                        id="geminiApiKey"
                        type="password"
                        autoComplete="off"
                        disabled={!isEditing}
                        defaultValue=""
                        helperText={keyHelperText(
                          company.hasGeminiKey === true,
                        )}
                      />
                    </GridItem>
                  </Grid>
                </Box>
              </GridItem>

              {/* AI Memory Section */}
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <Box
                  borderWidth="1px"
                  borderColor={{ base: "gray.200", _dark: "whiteAlpha.200" }}
                  borderRadius="md"
                  p={4}
                  bg={{ base: "gray.50", _dark: "whiteAlpha.50" }}
                >
                  <HStack justify="space-between" align="flex-start" gap={4}>
                    <Stack gap={1}>
                      <Heading
                        size="sm"
                        color={{ base: "navy.700", _dark: "white" }}
                      >
                        {t("app.company.memory.title")}
                      </Heading>
                      <Text
                        fontSize="sm"
                        color={{ base: "gray.600", _dark: "gray.400" }}
                      >
                        {t("app.company.memory.description")}
                      </Text>
                    </Stack>
                    <Switch
                      checked={memoryEnabled}
                      disabled={!isEditing}
                      onCheckedChange={(e: any) => setMemoryEnabled(e.checked)}
                    />
                  </HStack>
                </Box>
              </GridItem>
            </Grid>

            {!isEditing && (
              <Grid
                templateColumns="repeat(2, 1fr)"
                gap={4}
                mt={6}
                pt={4}
                borderTopWidth="1px"
                borderColor={{ base: "gray.200", _dark: "whiteAlpha.100" }}
                fontSize="sm"
              >
                <GridItem>
                  <Text
                    color={{ base: "gray.600", _dark: "gray.400" }}
                    display="inline"
                  >
                    {t("app.company.created")}
                  </Text>
                  <Text
                    ml={2}
                    color={{ base: "navy.700", _dark: "white" }}
                    display="inline"
                  >
                    {new Date(company.createdAt).toLocaleDateString()}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    color={{ base: "gray.600", _dark: "gray.400" }}
                    display="inline"
                  >
                    {t("app.company.lastUpdated")}
                  </Text>
                  <Text
                    ml={2}
                    color={{ base: "navy.700", _dark: "white" }}
                    display="inline"
                  >
                    {new Date(company.updatedAt).toLocaleDateString()}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text
                    color={{ base: "gray.600", _dark: "gray.400" }}
                    display="inline"
                  >
                    {t("app.company.status")}
                  </Text>
                  <Box
                    ml={2}
                    display="inline-block"
                    borderRadius="full"
                    px={2}
                    py={1}
                    fontSize="xs"
                    fontWeight="medium"
                    bg={
                      company.isActive
                        ? { base: "green.100", _dark: "green.900" }
                        : { base: "gray.100", _dark: "gray.700" }
                    }
                    color={
                      company.isActive
                        ? { base: "green.700", _dark: "green.300" }
                        : { base: "gray.700", _dark: "gray.400" }
                    }
                  >
                    {company.isActive
                      ? t("app.company.active")
                      : t("app.company.inactive")}
                  </Box>
                </GridItem>
                {company.userCount !== undefined && (
                  <GridItem>
                    <Text
                      color={{ base: "gray.600", _dark: "gray.400" }}
                      display="inline"
                    >
                      {t("app.company.totalUsers")}
                    </Text>
                    <Text
                      ml={2}
                      color={{ base: "navy.700", _dark: "white" }}
                      display="inline"
                    >
                      {company.userCount}
                    </Text>
                  </GridItem>
                )}
              </Grid>
            )}

            {isEditing && (
              <HStack gap={3} mt={6}>
                <Button
                  type="button"
                  onClick={handleCancel}
                  variant="subtle"
                  colorPalette="gray"
                  flex={1}
                >
                  <MdCancel size="20px" />
                  {t("app.company.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  variant="solid"
                  colorPalette="blue"
                  flex={1}
                >
                  <MdSave size="20px" />
                  {saving
                    ? t("app.company.saving")
                    : t("app.company.saveChanges")}
                </Button>
              </HStack>
            )}
          </form>
        </Card.Body>
      </Card.Root>

      {/* Security — company-wide two-factor authentication requirement (Admin only) */}
      <Card.Root>
        <Card.Body p={6}>
          <HStack justify="space-between" align="flex-start" gap={4}>
            <Stack gap={1}>
              <Heading size="sm" color={{ base: "navy.700", _dark: "white" }}>
                {t("app.twoFactor.companyRequireTitle")}
              </Heading>
              <Text
                fontSize="sm"
                color={{ base: "gray.600", _dark: "gray.400" }}
              >
                {t("app.twoFactor.companyRequireDescription")}
              </Text>
              {!isAdmin && (
                <Text
                  fontSize="xs"
                  color={{ base: "gray.500", _dark: "gray.500" }}
                >
                  {t("app.twoFactor.companyAdminOnly")}
                </Text>
              )}
            </Stack>
            <Switch
              checked={require2FA}
              disabled={!isAdmin || saving2fa}
              onCheckedChange={(e: any) => handleToggle2FA(e.checked)}
            />
          </HStack>
        </Card.Body>
      </Card.Root>
    </Flex>
  );
};

export default CompanyDetails;
